/**
 * Copy for the five surfaces. Sentence case, procedural voice — no marketing.
 *
 * **No `spec` field.** Each entry used to carry one — `SPEC · FIL-1 … FIL-11`,
 * rendered as an eyebrow above the title — so a reader could get from a
 * half-built screen to the contract behind it. That was scaffolding for an
 * audience of one, and all four surfaces are finished; to the deputy who
 * actually uses this, a requirement id above the page title reads as a product
 * that has not shipped. `surfaces.test.ts` holds the line.
 *
 * A summary says what the surface is *for*, not how it is built. The Files
 * entry named SHA-256 verification and no longer does: that is a fact about a
 * file, and it belongs on the row where the hash is (FIL-6), not in the
 * sentence that has to reassure someone before they have opened anything.
 *
 * **Files carries no summary at all.** Its old title-plus-sentence pair
 * ("Files" / "Securely exchange files…") said the same thing twice — a name
 * and then a sentence unpacking the name. `title` is the headline now, doing
 * both jobs in one line; `SurfaceHeader` renders no second line when
 * `summary` is absent.
 *
 * **Files no longer carries an `actions` label either**, and that string's
 * removal is the record of a real change. It named a `role="group"` holding two
 * controls — Connect an agency beside the recycle bin. Connect moved to
 * Spaces (ADR-0032), which left a *named* group around a single button that
 * `RecycleBinButton` renders as nothing on an empty bin: a screen reader was
 * being told the name of an empty container. One control needs no group.
 */
export const SURFACE_COPY = {
  files: {
    /**
     * **The headline names the rooms, not the counterparties.** It read
     * "Agencies available for secure file sharing" — a list of *agencies*, which
     * is what this surface held while a space was a link between exactly two of
     * them, and then "Spaces shared with other agencies" once what it listed was
     * spaces (CON-22, ADR-0066). `FolderDestinations` is what renders here now
     * (FIL-1d, FIL-1e) — the space table and grid both left for Spaces
     * (ADR-0032), and a deputy's one act is opening a folder — so the headline
     * names what is actually in the list. The agencies stay in the sentence
     * rather than leading it, unchanged.
     */
    title: 'Folders shared with other agencies',
  },
  /**
   * **A name, not a headline** — the opposite call from Files above it, and the
   * same one Users makes. Files is where the product's promise is made to
   * whoever opens the app; Spaces is a settings screen an admin navigated to
   * on purpose, and it owes them a label and a sentence, not a pitch.
   *
   * **The title caught up with the summary here** (ADR-0071's last
   * consequence). The surface was called Agencies while a row on it was a
   * counterpart agency; a row is a room with a name, an owner and a roster
   * (CON-22), and the sentence below had already said so for a milestone.
   */
  spaces: {
    title: 'Folders',
    summary: 'Open a shared folder, and manage the agencies working in each one.',
  },
  users: {
    title: 'Users',
    summary: 'Keep the right people connected, with the right access.',
  },
  // The Recipients tab used to sit here, as the one non-surface in this map.
  // It became real, moved its copy into `components/Users/users.copy.ts`, and
  // is now withdrawn outright (ADR-0072) — so what this map holds is, again and
  // for good, exactly the surfaces `SURFACES` carries.
  // **"Estimated cost" was the wrong headline, in STO-4's own words**, and this
  // entry carried it until the surface became real. §17 replaced the single
  // global per-GB rate with plan attributes, so an agency inside its allowance
  // owes nothing marginal — what it needs is how much room is left, not a
  // multiplication it cannot check. Nothing on this surface names a price.
  // **The summary names the three questions the surface answers, in the order it
  // answers them.** "Keep your storage and transfers on track" described a
  // posture rather than a use — true of the page but no help to somebody opening
  // it. What an admin arrives wanting is what is stored, what can be got back,
  // and where the capacity has gone.
  storage: {
    title: 'Storage',
    summary:
      'See what your agency is storing, what can be reclaimed, and where your capacity is going.',
  },
  audit: {
    title: 'Audit',
    summary: 'Every action accounted for. Every handoff defensible.',
  },
} as const;
